import React from "react";
import { playClick } from "@/context/AuthContext";
import { cn } from "@/lib/utils";

export function LuxuryButton({ children, variant = "primary", className, onClick, type = "button", disabled, size = "md", ...props }) {
  const sizes = { sm: "px-4 py-2 text-sm", md: "px-6 py-3 text-sm", lg: "px-8 py-4 text-base" };
  const variants = { primary: "btn3d-primary", gold: "btn3d-gold", maroon: "btn3d-maroon", dark: "btn3d-dark" };
  return (
    <button
      type={type}
      disabled={disabled}
      onClick={(e) => { playClick(); onClick && onClick(e); }}
      className={cn("btn3d", variants[variant] || "btn3d-primary", sizes[size], className)}
      {...props}
    >
      {children}
    </button>
  );
}

export function GlassCard({ children, className, shine = false, hover = false, ...props }) {
  return (
    <div className={cn("glass rounded-2xl", shine && "shine", hover && "card-hover", className)} {...props}>
      {children}
    </div>
  );
}

export function MetricCard({ label, value, sub, icon: Icon, accent = false, tone = "gold", className }) {
  const variant = accent ? "gold" : (["gold", "royal", "maroon", "white"].includes(tone) ? tone : "white");
  return (
    <div className={cn("metric-card", `metric-${variant}`, className)}>
      <div className="flex items-start justify-between">
        <div>
          <p className="m-label text-xs font-semibold uppercase tracking-wider">{label}</p>
          <p className="m-value mt-2 text-2xl font-extrabold">{value}</p>
          {sub && <p className="m-sub mt-1 text-xs font-medium">{sub}</p>}
        </div>
        {Icon && (
          <div className="m-icon grid h-11 w-11 place-items-center rounded-xl">
            <Icon size={20} />
          </div>
        )}
      </div>
    </div>
  );
}

const badgeMap = {
  active: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  approved: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  paid: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  success: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  completed: "text-emerald-300 border-emerald-400/30 bg-emerald-400/10",
  pending: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  requested: "text-amber-300 border-amber-400/30 bg-amber-400/10",
  processing: "text-blue-300 border-blue-400/30 bg-blue-400/10",
  under_review: "text-blue-300 border-blue-400/30 bg-blue-400/10",
  rejected: "text-rose-300 border-rose-400/30 bg-rose-400/10",
  cancelled: "text-rose-300 border-rose-400/30 bg-rose-400/10",
  suspended: "text-rose-300 border-rose-400/30 bg-rose-400/10",
  error: "text-rose-300 border-rose-400/30 bg-rose-400/10",
};

export function StatusBadge({ status }) {
  const key = (status || "").toLowerCase();
  const cls = badgeMap[key] || "text-slate-300 border-white/15 bg-white/5";
  return (
    <span className={cn("inline-flex items-center rounded-full border px-2.5 py-1 text-[11px] font-medium capitalize", cls)}>
      {(status || "").replace(/_/g, " ")}
    </span>
  );
}

export function ProgressBar({ value = 0, max = 100, className, gold = true }) {
  const pct = Math.max(0, Math.min(100, max > 0 ? (value / max) * 100 : 0));
  return (
    <div className={cn("h-2.5 w-full overflow-hidden rounded-full bg-white/8", className)}>
      <div
        className="h-full rounded-full transition-all duration-700"
        style={{
          width: `${pct}%`,
          background: gold
            ? "linear-gradient(90deg, #f0d9a6, #d9b36c)"
            : "linear-gradient(90deg, #7c6bf5, #4d7cff)",
          boxShadow: "0 0 14px rgba(217,179,108,0.5)",
        }}
      />
    </div>
  );
}

export function PageTitle({ title, subtitle, right }) {
  return (
    <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
      <div>
        <h1 className="font-display text-2xl font-semibold text-white sm:text-3xl">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-[var(--text-dim)]">{subtitle}</p>}
      </div>
      {right}
    </div>
  );
}

export function SkeletonGrid({ rows = 4 }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="skeleton h-28" />
      ))}
    </div>
  );
}

export function EmptyState({ icon: Icon, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center gap-3 py-16 text-center">
      {Icon && <Icon size={40} className="text-[var(--text-dim)]" />}
      <p className="text-white">{title}</p>
      {hint && <p className="max-w-sm text-sm text-[var(--text-dim)]">{hint}</p>}
    </div>
  );
}
